import React, { MouseEvent } from 'react';
import { connect } from 'react-redux';
import { Box } from '@mui/material';

import { captureHandledException } from '@paragon/client-tools';
import { hasTrussDesignPermission as hasTrussDesignPermissionSelector } from '@paragon/login-tools';

import SceneMenu from '../components/misc/menus/SceneMenu';
import { PendingPickedPointMovementPopover } from '../components/misc/PendingPickedPointMovementPopover';
import { SceneTooltip } from '../components/misc/tooltips/SceneTooltip';
import { RootState } from '../store';
import { getSceneCanvasCursorOverride } from '../store/selectors/layoutModeCursorSelectors';
import { ReactThreeFiberBridge } from './ReactThreeFiberBridge';
import { SceneWrapper } from './SceneWrapper';

type Props = ReturnType<typeof mapStateToProps>;

class SceneCanvas extends React.Component<Props> {
  canvasElement: HTMLCanvasElement | null = null;
  viewportDiv: HTMLDivElement | null = null;

  componentDidCatch(error: Error) {
    if ((error as Error)?.message === 'Error creating WebGL context.') {
      document.body.innerHTML =
        '<p>It seems like we\'re unable to use WebGL for 3D graphics on your computer. Please make sure that your browser and video card drivers are up to date.  <p>You can double-check by visiting <a href="https://get.webgl.org/webgl2/" style="color: white;">https://get.webgl.org/webgl2/</a>.  If that site works fine but we\'re still having problems please contact Paragon Customer Support.';
    } else {
      captureHandledException(error);
    }
  }

  render() {
    return (
      <Box
        id="scene-canvas"
        ref={(element: HTMLDivElement) => {
          this.viewportDiv = element;
        }}
        sx={{
          height: '100%',
          width: '100%',
          position: 'relative',
          zIndex: 0,
          lineHeight: 0,
          cursor: this.props.cursorOverride ?? 'crosshair',
          userSelect: 'none',
          minWidth: '5px',
          minHeight: '5px',

          // Either ThreeJsDom (in design mode) or the camera controls (in layout mode) will be handling touchscreen
          //   manipulations. We don't want mobile browsers zooming or panning the entire app when a user is interacting
          //   with the scene. Do not remove this without extensive testing.
          touchAction: 'none',
        }}
        onContextMenu={(e: MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
        }}
      >
        <SceneWrapper
          canvasRef={(canvas) => {
            this.canvasElement = canvas;
          }}
        >
          <ReactThreeFiberBridge getCanvasElement={() => this.canvasElement} getViewportDiv={() => this.viewportDiv} />
        </SceneWrapper>
        <SceneTooltip />
        <SceneMenu />
        <PendingPickedPointMovementPopover />
      </Box>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  hasTrussDesignPermission: hasTrussDesignPermissionSelector(state),
  cursorOverride: getSceneCanvasCursorOverride(state),
});

export default connect(mapStateToProps)(SceneCanvas);
